import React from "react";
import {
  Drawer,
  Box,
  Tabs,
  Tab,
  useTheme,
  Paper,
  Stack,
  Typography,
  IconButton,
  TextField,
  MenuItem,
  Autocomplete,
  Switch,
} from "@mui/material";
import Avatar from "./Avatar";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`sliding-sidebar-tabpanel-${index}`}
      aria-labelledby={`sliding-sidebar-tab-${index}`}
      style={{ height: "100%" }}
      {...other}
    >
      {value === index && children}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `sliding-sidebar-tab-${index}`,
    "aria-controls": `sliding-sidebar-tabpanel-${index}`,
  };
}

export interface TabConfig {
  label: React.ReactNode;
  component: React.ReactNode;
}

interface SlidingSidebarProps {
  open: boolean;
  onClose: () => void;
  tabs?: TabConfig[];
  activeTab?: number;
  onTabChange?: (newValue: number) => void;
  width?: string;
  maxWidth?: string;
  children?: React.ReactNode;
}

interface SidebarAvatarProps {
  src: string;
  alt: string;
  onEditAvatar?: () => void;
}

export interface EditField {
  label: string;
  value:
    | string
    | number
    | Array<{ label: string; value: string | number }>
    | null;
  field: string;
  type?: "text" | "select" | "autocomplete" | "multiselect" | "password";
  options?: Array<{ label: string; value: string | number }>;
  disabled?: boolean;
  required?: boolean;
  multiple?: boolean;
  onChange?: (value: any) => void;
  renderRight?: React.ReactNode;
}

interface EditSection {
  title?: string;
  fields: EditField[];
}

interface SidebarEditFormProps {
  sections: EditSection[];
  onValueChange: (field: string, value: any) => void;
}

interface SwitchControlProps {
  label: string;
  checked: boolean;
  onChange: () => void;
}

const SlidingSidebar: React.FC<SlidingSidebarProps> = ({
  open,
  onClose,
  tabs,
  activeTab = 0,
  onTabChange,
  width = "42%",
  maxWidth = "800px",
  children,
}) => {
  const theme = useTheme();

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    onTabChange?.(newValue);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width,
          maxWidth,
          bgcolor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          mt: "48px",
        },
      }}
    >
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        {tabs ? (
          <>
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 1,
                bgcolor: theme.palette.background.paper,
                borderBottom: 1,
                borderColor: "divider",
                pt: 2,
              }}
            >
              <Tabs value={activeTab} onChange={handleChange}>
                {tabs.map((tab, index) => (
                  <Tab key={index} label={tab.label} {...a11yProps(index)} />
                ))}
              </Tabs>
            </Box>

            <Box
              sx={{
                flexGrow: 1,
                overflow: "auto",
                display: "flex",
                flexDirection: "column",
                pb: "100px",
              }}
            >
              {tabs.map((tab, index) => (
                <TabPanel key={index} value={activeTab} index={index}>
                  <Box
                    sx={{
                      height: "100%",
                      p: index !== 0 ? 3 : 0,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {tab.component}
                  </Box>
                </TabPanel>
              ))}
            </Box>
          </>
        ) : (
          <Box
            sx={{
              flexGrow: 1,
              overflow: "auto",
              p: 3,
              display: "flex",
              flexDirection: "column",
              pb: "100px",
            }}
          >
            {children}
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export const SidebarSection: React.FC<{
  title?: string;
  children: React.ReactNode;
}> = ({ title, children }) => {
  return (
    <Stack spacing={0.5} sx={{ p: 2 }}>
      <Paper elevation={0} sx={{ p: 1 }}>
        <Stack spacing={2}>
          {title && <Typography variant="h6">{title}</Typography>}
          {children}
        </Stack>
      </Paper>
    </Stack>
  );
};

export const ButtonControls: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        ...theme.components?.MuiButtonControls?.styleOverrides?.root,
        padding: "0px 40px 50px 24px",
        borderRadius: 2,
        height: "70px",
        boxShadow: 3,
        backgroundColor: theme.palette.background.paper,
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 1.2,
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
      }}
    >
      {children}
    </Box>
  );
};

export const SidebarAvatar: React.FC<SidebarAvatarProps> = ({
  src,
  alt,
  onEditAvatar,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        position: "relative",
        width: "150px",
        height: "150px",
        margin: "24px auto",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Avatar src={src} alt={alt} size={150} />
      {onEditAvatar && (
        <IconButton
          onClick={onEditAvatar}
          sx={{
            position: "absolute",
            bottom: 0,
            right: 0,
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
            },
            width: "36px",
            height: "36px",
          }}
        >
          <PhotoCameraIcon />
        </IconButton>
      )}
    </Box>
  );
};

export const SidebarEditForm: React.FC<SidebarEditFormProps> = ({
  sections,
  onValueChange,
}) => {
  const theme = useTheme();

  const renderField = (field: EditField) => {
    const commonProps = {
      key: field.field,
      label: field.label,
      value: field.value || "",
      onChange: (e: any) => onValueChange(field.field, e.target.value),
      size: "small" as const,
      fullWidth: true,
      disabled: field.disabled,
      required: field.required,
      variant: "outlined" as const,
      InputLabelProps: {
        shrink: true,
      },
      sx: {
        ...theme.components?.MuiSidebarEditForm?.styleOverrides?.field,
        "& .MuiOutlinedInput-root": {
          backgroundColor: "white",
        },
      },
    };

    const fieldContent = (() => {
      if (field.type === "select") {
        return (
          <TextField {...commonProps} select>
            {field.options?.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        );
      }

      if (field.type === "autocomplete") {
        return (
          <Autocomplete
            options={field.options || []}
            getOptionLabel={(option) => option.label}
            value={
              field.options?.find((opt) => opt.value === field.value) || null
            }
            onChange={(_, newValue) =>
              onValueChange(field.field, newValue?.value)
            }
            disabled={field.disabled}
            disablePortal
            renderInput={(params) => <TextField {...params} {...commonProps} />}
            sx={theme.components?.MuiSidebarEditForm?.styleOverrides?.field}
          />
        );
      }

      if (field.type === "multiselect") {
        return (
          <Autocomplete
            multiple
            options={field.options || []}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            value={Array.isArray(field.value) ? field.value : []}
            onChange={(_, newValue) => onValueChange(field.field, newValue)}
            disabled={field.disabled}
            renderInput={(params) => <TextField {...params} {...commonProps} />}
          />
        );
      }

      return <TextField {...commonProps} type={field.type} />;
    })();

    return (
      <Box
        key={field.field}
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 1, // Reduced margin between fields
        }}
      >
        <Box sx={{ flex: 1 }}>{fieldContent}</Box>
        {field.renderRight}
      </Box>
    );
  };

  return (
    <Stack
      spacing={0.5}
      sx={theme.components?.MuiSidebarEditForm?.styleOverrides?.root}
    >
      {sections.map((section, sectionIndex) => (
        <Paper
          key={sectionIndex}
          elevation={0}
          sx={{
            ...theme.components?.MuiSidebarEditForm?.styleOverrides?.section,
            p: 2, // Add consistent padding
          }}
        >
          <Stack spacing={1}>
            {" "}
            {/* Reduced spacing between elements */}
            {section.title && (
              <Typography variant="h6" sx={{ mb: 1 }}>
                {section.title}
              </Typography>
            )}
            {section.fields.map(renderField)}
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
};

export const SwitchControl: React.FC<SwitchControlProps> = ({
  label,
  checked,
  onChange,
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Typography>{label}</Typography>
      <Switch checked={checked} onChange={onChange} color="primary" />
    </Box>
  );
};

export default SlidingSidebar;
